import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronLeft, Check, Crown, Undo2, Trash2, FileDown, ShieldCheck } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { MEDICAL_STEPS, DECLARATION, BODY_FRONT, BODY_BACK, BODY_MARKERS, MEDICAL_LABELS } from '../data/medicalForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOTAL = MEDICAL_STEPS.length + 1; // + declaration step

const todayIso = () => new Date().toISOString().slice(0, 10);

const MedicalDisclosurePage = () => {
  const { user, hasAccess, loading, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [bodyMap, setBodyMap] = useState([]);
  const [marker, setMarker] = useState('pain');
  const [bodyView, setBodyView] = useState('front');
  const [signedName, setSignedName] = useState('');
  const [signedDate, setSignedDate] = useState(todayIso());
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user?.id) refreshUser(user.id); /* eslint-disable-next-line */ }, []);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API}/medical-disclosure/${user.id}`);
      const d = await res.json();
      if (d.exists) {
        setAnswers(d.answers || {});
        setBodyMap(d.body_map || []);
        setSignedName(d.signed_name || '');
        setSignedDate(d.signed_date || todayIso());
      } else {
        setAnswers((a) => ({ ...a, name: user.name || '', email: user.email || '' }));
      }
    } catch (e) { console.error(e); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const set = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));
  const toggleCheck = (key, opt) => setAnswers((a) => {
    const arr = Array.isArray(a[key]) ? a[key] : [];
    return { ...a, [key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
  });

  const placeMarker = (e, view) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBodyMap((m) => [...m, { view, x, y, type: marker }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/medical-disclosure`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, user_email: user.email, answers, body_map: bodyMap, signed_name: signedName, signed_date: signedDate }),
      });
      if (res.ok) setSaved(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), m = 46;
    let y = m;
    const ln = (t, x, s = 11, b = false, c = [60, 60, 60]) => {
      doc.setFont('helvetica', b ? 'bold' : 'normal'); doc.setFontSize(s); doc.setTextColor(...c);
      doc.splitTextToSize(t, W - m - x).forEach((w) => { if (y > H - m) { doc.addPage(); y = m; } doc.text(w, x, y); y += s + 4; });
    };
    ln('Grounded Wellness with Jill', m, 18, true, [44, 62, 46]);
    ln('Health & Medical Disclosure', m, 12, false, [140, 110, 90]); y += 6;
    MEDICAL_STEPS.forEach((st) => {
      const rows = st.fields.filter((f) => { const v = answers[f.key]; return Array.isArray(v) ? v.length : (v !== undefined && v !== '' && v !== null); });
      if (!rows.length && st.type !== 'bodymap') return;
      if (y > H - m - 30) { doc.addPage(); y = m; }
      ln(st.title, m, 13, true, [44, 62, 46]);
      rows.forEach((f) => {
        const v = answers[f.key];
        ln(`${f.label}: ${Array.isArray(v) ? v.join(', ') : v}`, m + 8, 10);
      });
      if (st.type === 'bodymap' && bodyMap.length) {
        ln('Body map markers:', m + 8, 10, true);
        bodyMap.forEach((mk) => {
          const lbl = (BODY_MARKERS.find((b) => b.type === mk.type) || {}).label || mk.type;
          const hpos = mk.x < 40 ? 'left' : mk.x > 60 ? 'right' : 'centre';
          const vpos = mk.y < 34 ? 'upper' : mk.y > 66 ? 'lower' : 'mid';
          ln(`• ${lbl} — ${mk.view}, ${vpos} ${hpos}`, m + 16, 10, false, [110, 110, 110]);
        });
      }
      y += 6;
    });
    if (y > H - m - 30) { doc.addPage(); y = m; }
    ln('Declaration', m, 13, true, [138, 106, 90]);
    ln(`Signed: ${signedName || '—'}    Date: ${signedDate || '—'}`, m + 8, 11, true);
    doc.save('health-medical-disclosure.pdf');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500 font-body">Loading…</div>;

  if (!user || !hasAccess('vip')) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="medical-access-gate">
        <div className="max-w-xl mx-auto text-center bg-[#f5efe4] border border-stone-200 rounded-2xl p-10">
          <Crown size={26} className="text-forest mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-forest mb-2">Health & Medical Disclosure</h1>
          <p className="font-body text-stone-600 text-sm mb-6">Available to Jill's V.I.P. members & package holders.</p>
          <Link to="/programs" className="inline-flex items-center gap-2 bg-forest text-cream font-body px-6 py-3 rounded-full">Back to Programs</Link>
        </div>
      </div>
    );
  }

  const isDeclaration = step === MEDICAL_STEPS.length;
  const current = MEDICAL_STEPS[step];
  const pct = Math.round(((step + 1) / TOTAL) * 100);

  const renderField = (f) => {
    const v = answers[f.key];
    if (f.type === 'text' || f.type === 'num') return (
      <input type={f.type === 'num' ? 'number' : 'text'} value={v || ''} onChange={(e) => set(f.key, e.target.value)}
        className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
        data-testid={`med-field-${f.key}`} />
    );
    if (f.type === 'textarea') return (
      <textarea rows={3} value={v || ''} onChange={(e) => set(f.key, e.target.value)}
        className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
        data-testid={`med-field-${f.key}`} />
    );
    const opts = f.type === 'ynp' ? ['Yes', 'No', 'Prefer not to say'] : f.options;
    if (f.type === 'checks') return (
      <div className="flex flex-wrap gap-2" data-testid={`med-field-${f.key}`}>
        {opts.map((o) => {
          const on = Array.isArray(v) && v.includes(o);
          return (
            <button key={o} type="button" onClick={() => toggleCheck(f.key, o)}
              className={`px-3 py-1.5 rounded-full font-body text-xs border transition-colors ${on ? 'bg-forest text-cream border-forest' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40'}`}
              data-testid={`med-check-${f.key}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              {on && <Check size={11} className="inline mr-1" />}{o}
            </button>
          );
        })}
      </div>
    );
    // radio / ynp
    return (
      <div className="flex flex-wrap gap-2" data-testid={`med-field-${f.key}`}>
        {opts.map((o) => (
          <button key={o} type="button" onClick={() => set(f.key, o)}
            className={`px-4 py-1.5 rounded-full font-body text-xs border transition-colors ${v === o ? 'bg-forest text-cream border-forest' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40'}`}
            data-testid={`med-radio-${f.key}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
            {o}
          </button>
        ))}
      </div>
    );
  };

  const BodyDiagram = ({ view, src }) => (
    <div className="flex-1">
      <p className="text-center font-body text-xs uppercase tracking-wider text-stone-500 mb-2">{view === 'front' ? 'Front view' : 'Back view'}</p>
      <div className="relative mx-auto max-w-[220px] cursor-crosshair select-none" onClick={(e) => placeMarker(e, view)} data-testid={`body-map-${view}`}>
        <img src={src} alt={`${view} body`} className="w-full pointer-events-none" draggable={false} />
        {bodyMap.filter((m) => m.view === view).map((mk, i) => {
          const md = BODY_MARKERS.find((b) => b.type === mk.type) || {};
          return (
            <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 font-bold text-base leading-none"
              style={{ left: `${mk.x}%`, top: `${mk.y}%`, color: md.color }}>{md.symbol}</span>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream py-10 px-4" data-testid="medical-disclosure-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/programs/vip/start-here" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-6">
          <ArrowLeft size={16} /> Back to Start Here
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} className="text-[#8a6a5a]" />
          <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">Health & Medical Disclosure</p>
        </div>
        <p className="font-body text-xs text-stone-500 mb-4 max-w-xl">
          This helps Jill adapt your sessions safely. It is not a diagnostic assessment — you only share what you're comfortable with.
        </p>

        {/* progress */}
        <div className="mb-6">
          <div className="flex justify-between text-[11px] font-body text-stone-400 mb-1">
            <span>Step {step + 1} of {TOTAL}</span><span>{pct}%</span>
          </div>
          <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
            <div className="h-full bg-forest transition-all" style={{ width: `${pct}%` }} data-testid="med-progress" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
            className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
            {isDeclaration ? (
              <div data-testid="med-step-declaration">
                <h2 className="font-heading text-2xl text-forest mb-4">Client Declaration</h2>
                <ul className="space-y-2 mb-6">
                  {DECLARATION.map((d, i) => (
                    <li key={i} className="font-body text-xs text-stone-600 leading-relaxed flex gap-2">
                      <Check size={14} className="text-forest flex-shrink-0 mt-0.5" /> {d}
                    </li>
                  ))}
                </ul>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Type your full name</label>
                    <input value={signedName} onChange={(e) => setSignedName(e.target.value)}
                      className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest" data-testid="med-signed-name" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Date</label>
                    <input type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)}
                      className="font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest" data-testid="med-signed-date" />
                  </div>
                </div>
                {saved && (
                  <div className="mt-4 bg-forest/10 border border-forest/20 rounded-lg px-4 py-3 font-body text-sm text-forest" data-testid="med-saved-msg">
                    ✓ Saved to your profile. You can download a copy below.
                  </div>
                )}
                <div className="flex flex-wrap gap-3 mt-5">
                  <button onClick={save} disabled={!signedName || saving}
                    className="bg-forest text-cream font-body text-sm font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-50" data-testid="med-submit">
                    {saving ? 'Saving…' : saved ? 'Save again' : 'Submit & save'}
                  </button>
                  <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 bg-[#8a6a5a] text-cream font-body text-sm px-5 py-2.5 rounded-full hover:bg-[#755848] transition-colors" data-testid="med-download">
                    <FileDown size={14} /> Download PDF
                  </button>
                </div>
              </div>
            ) : current.type === 'bodymap' ? (
              <div data-testid="med-step-bodymap">
                <h2 className="font-heading text-2xl text-forest mb-2">{current.title}</h2>
                <p className="font-body text-xs text-stone-500 mb-4">Choose a marker, then tap the diagrams to place it wherever you feel it.</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {BODY_MARKERS.map((mk) => (
                    <button key={mk.type} type="button" onClick={() => setMarker(mk.type)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-xs border transition-colors ${marker === mk.type ? 'bg-forest text-cream border-forest' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40'}`}
                      data-testid={`body-marker-${mk.type}`}>
                      <span className="font-bold" style={{ color: marker === mk.type ? '#fff' : mk.color }}>{mk.symbol}</span> {mk.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-4">
                  <BodyDiagram view="front" src={BODY_FRONT} />
                  <BodyDiagram view="back" src={BODY_BACK} />
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button type="button" onClick={() => setBodyMap((m) => m.slice(0, -1))} className="inline-flex items-center gap-1.5 text-xs font-body text-stone-500 hover:text-forest" data-testid="body-map-undo"><Undo2 size={13} /> Undo</button>
                  <button type="button" onClick={() => setBodyMap([])} className="inline-flex items-center gap-1.5 text-xs font-body text-stone-500 hover:text-red-600" data-testid="body-map-clear"><Trash2 size={13} /> Clear all</button>
                  <span className="ml-auto text-[11px] font-body text-stone-400">{bodyMap.length} marker{bodyMap.length === 1 ? '' : 's'}</span>
                </div>
                <div className="mt-4">
                  <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Additional notes</label>
                  {renderField(current.fields[0])}
                </div>
              </div>
            ) : (
              <div data-testid={`med-step-${current.id}`}>
                <h2 className="font-heading text-2xl text-forest mb-1">{current.title}</h2>
                {current.note && <p className="font-body text-xs text-stone-500 mb-4 italic">{current.note}</p>}
                <div className="space-y-4 mt-3">
                  {current.fields.map((f) => (
                    <div key={f.key}>
                      <label className="block font-body text-sm text-stone-700 mb-1.5">{f.label}</label>
                      {renderField(f)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="inline-flex items-center gap-1.5 font-body text-sm text-stone-500 hover:text-forest disabled:opacity-40" data-testid="med-back">
            <ChevronLeft size={16} /> Back
          </button>
          {!isDeclaration && (
            <button onClick={() => setStep((s) => Math.min(TOTAL - 1, s + 1))}
              className="inline-flex items-center gap-1.5 bg-forest text-cream font-body text-sm font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors" data-testid="med-next">
              Next <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalDisclosurePage;
